﻿import {
  AlertCircle,
  FileWarning,
  Loader2,
  MailWarning,
  Plus,
  Store,
} from "lucide-react";
import { Link } from "react-router-dom";
import { useT } from "@/lib/i18n";
import type { ShopStatus } from "@ticketnjoy/types";

import { DashboardShell } from "@/components/dashboard-shell";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { useAuthenticatedMe } from "@/features/organizers/api";

const STATUS_VARIANTS: Record<ShopStatus, "success" | "warning" | "secondary"> = {
  Active: "success",
  Paused: "warning",
  Archived: "secondary",
};

export function HomePage() {
  const { data, isLoading } = useAuthenticatedMe();
  const t = useT();

  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!data) {
    // Hata varsa useAuthenticatedMe zaten /login'e yönlendiriyor.
    return null;
  }

  return (
    <DashboardShell companyName={data.companyName}>
      {!data.emailVerified && (
        <Card className="mb-6 border-amber-200 bg-amber-50 dark:border-amber-900 dark:bg-amber-950/30">
          <CardHeader className="flex flex-row items-start gap-3 space-y-0">
            <MailWarning className="mt-0.5 h-5 w-5 text-amber-600" />
            <div>
              <CardTitle className="text-base">
                {t("emailVerification.title")}
              </CardTitle>
              <CardDescription>
                {t("emailVerification.description")}
              </CardDescription>
            </div>
          </CardHeader>
        </Card>
      )}

      {/* Yasal bilgi eksikse üst banner. Email verification'dan ayrı,
          eş zamanlı ikisi birden görünebilir — ayrı aksiyonlar çünkü. */}
      {!data.isLegalInfoComplete && (
        <Card className="mb-6 border-amber-200 bg-amber-50 dark:border-amber-900 dark:bg-amber-950/30">
          <CardHeader className="flex flex-row items-start gap-3 space-y-0">
            <FileWarning className="mt-0.5 h-5 w-5 text-amber-600" />
            <div className="flex-1 space-y-3">
              <div>
                <CardTitle className="text-base">
                  {t("legalBanner.title")}
                </CardTitle>
                <CardDescription>
                  {t("legalBanner.description")}
                </CardDescription>
              </div>
              <Button size="sm" variant="outline" asChild>
                <Link to="/settings">{t("legalBanner.cta")}</Link>
              </Button>
            </div>
          </CardHeader>
        </Card>
      )}

      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold">{t("shops.title")}</h1>
          <p className="text-sm text-muted-foreground">
            {t("shops.subtitle", {
              count: data.shops.length,
              email: data.email,
            })}
          </p>
        </div>
        <Button asChild>
          <Link to="/shops/new">
            <Plus className="me-1.5 h-4 w-4" />
            {t("shops.new")}
          </Link>
        </Button>
      </div>

      {data.shops.length === 0 ? (
        <EmptyState />
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {data.shops.map((shop) => (
            <Card key={shop.id} className="transition-colors hover:border-primary/40">
              <CardHeader className="space-y-3">
                <div className="flex items-center justify-between">
                  <Store className="h-5 w-5 text-muted-foreground" />
                  <Badge variant={STATUS_VARIANTS[shop.status]}>
                    {t(`shops.status.${shop.status}`)}
                  </Badge>
                </div>
                <div>
                  <CardTitle className="text-lg">{shop.name}</CardTitle>
                  <CardDescription className="mt-1 font-mono text-xs">
                    /{shop.slug}
                  </CardDescription>
                </div>
              </CardHeader>
              <CardContent>
                <Button variant="outline" size="sm" className="w-full" asChild>
                  <Link to={`/shops/${shop.slug}`}>{t("common.manage")}</Link>
                </Button>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </DashboardShell>
  );
}

function EmptyState() {
  const t = useT("shops");
  return (
    <Card>
      <CardHeader className="items-center text-center">
        <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-muted">
          <AlertCircle className="h-6 w-6 text-muted-foreground" />
        </div>
        <CardTitle>{t("emptyTitle")}</CardTitle>
        <CardDescription>{t("emptyDescription")}</CardDescription>
      </CardHeader>
    </Card>
  );
}
